import { useEffect, type ComponentType, type JSX } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useMe, useMePartner } from "../../swr";
import { useWishlistStore } from "../../modules/products/domain/stores/useWishlistStore";
import { AppShell } from "../common/app-shell/shell";
import { NotFound } from "./routes/404";
import { DashboardIcon, ProfileIcon } from "../common/app-shell/icons";
import SettingsPage from "../../modules/settings/presentation/pages/settings_page";
import { SellerSettingsPage } from "../../modules/settings/presentation/pages/seller-settings-page";
import { AppRoutes } from "../../common/routes/app_routes";
import { footerRoutesBuilder } from "../../common/routes/footer_routes";
import type { UserDto } from "../../api/api";
import { Role } from "../../api/api";
import { ProductsPage } from "../../modules/products/presentation/pages/products_page";
import { AiSearchPage } from "../../modules/ai-search/presentation/pages/ai-search-page";
import { DashboardPage } from "../../modules/dashboard/presentation/pages/dashboard-page";
import { ProductDetailsPage } from "../../modules/products/presentation/pages/product_details_page";
import { ProductsComparePage } from "../../modules/products/presentation/pages/products_compare_page";
import { WishlistPage } from "../../modules/products/presentation/pages/wishlist-page";
import { MyProductsPage } from "../../modules/products/presentation/pages/my-products-page";
import { MyProductDetailsPage } from "../../modules/products/presentation/pages/my-product-details-page";
import { MyProductEditPage } from "../../modules/products/presentation/pages/my-product-edit-page";
import { MyProductNewPage } from "../../modules/products/presentation/pages/my-product-new-page";
import { MyProductBulkUploadPage } from "../../modules/products/presentation/pages/my-product-bulk-upload-page";
import { MyProductBulkUploadResultsPage } from "../../modules/products/presentation/pages/my-product-bulk-upload-results-page";
import { MyWarrantiesPage } from "../../modules/products/presentation/pages/my-warranties-page";
// ProductVariantsPage route disabled — must not be navigable
// import { ProductVariantsPage } from "../../modules/products/presentation/pages/product-variants-page";
import { PartnersPage } from "../../modules/partners/presentation/pages/partners_page";
import { CreatePartnerPage } from "../../modules/partners/presentation/pages/create-partner-page";
import { PartnerDetailsPage } from "../../modules/partners/presentation/pages/partner_details_page";
import { SalesPage } from "../../modules/sales/presentation/pages/sales_page";
import { SaleDetailsPage } from "../../modules/sales/presentation/pages/sale_details_page";
import { PurchaseOrdersPage } from "../../modules/purchase-orders/presentation/pages/purchase_orders_page";
import { PurchaseOrderDetailsPage } from "../../modules/purchase-orders/presentation/pages/purchase_order_details_page";
import { SellerOrderDetailsPage } from "../../modules/purchase-orders/presentation/pages/seller-order-details-page";
import { ConfirmDeliveryPage } from "../../modules/purchase-orders/presentation/pages/confirm-delivery-page";
import { CheckoutPage } from "../../modules/cart/presentation/pages/checkout_page";
import { OnboardingPage } from "../../modules/onboarding/presentation/pages/onboarding_page";
import { OnboardingGuard } from "./onboarding-guard";
import { UserDocumentsReviewPage } from "../../modules/admin-documents/presentation/pages/user-documents-review-page";
import { AdminDocumentsPage } from "../../modules/admin-documents/presentation/pages/admin-documents-page";
import { ProductRequestDetailsPage } from "../../modules/product-requests/presentation/pages/product-request-details-page";
import { AdminProductRequestsPage } from "../../modules/product-requests/presentation/pages/admin-product-requests-page";
import { UserProductRequestsReviewPage } from "../../modules/product-requests/presentation/pages/user-product-requests-review-page";
import { AdminProductRequestReviewPage } from "../../modules/product-requests/presentation/pages/admin-product-request-review-page";
import { LoyaltyPointsPage } from "../../modules/loyalty/presentation/pages/loyalty-points-page";
import { InboxPage } from "../../modules/inbox/presentation/pages/inbox-page";
import { QuotationsPage } from "../../modules/quotations/presentation/pages/quotations-page";
import { QuotationDetailPage } from "../../modules/quotations/presentation/pages/quotation-detail-page";
import { SellerDashboardPage } from "../../modules/seller-dashboard/presentation/pages/seller-dashboard-page";
import { SellerPaymentsPage } from "../../modules/payments/presentation/pages/seller-payments-page";
import { AdminLoyaltyConfigPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-config-page";
import { AdminLoyaltyPromotionsPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-promotions-page";
import { AdminLoyaltyPromotionDetailPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-promotion-detail-page";
import { AdminLoyaltyPromotionEditPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-promotion-edit-page";
import { AdminLoyaltySettingsPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-settings-page";
import { AdminLoyaltyAnalyticsPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-analytics-page";
import { AdminLoyaltyTransactionDetailPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-transaction-detail-page";
import { AdminLoyaltyRedemptionDetailPage } from "../../modules/loyalty/presentation/pages/admin-loyalty-redemption-detail-page";
import { AdminCatalogPage } from "../../modules/admin-catalog/presentation/pages/admin-catalog-page";
import { AdminFeaturedDealsPage } from "../../modules/featured-deals/presentation/pages/admin-featured-deals-page";
import { AdminPaymentsPage } from "../../modules/payments/presentation/pages/admin-payments-page";
import { AdminPaymentDetailsPage } from "../../modules/payments/presentation/pages/admin-payment-details-page";
import { AdminSupplierBillsPage } from "../../modules/payments/presentation/pages/admin-supplier-bills-page";
import { AdminDashboardPage } from "../../modules/admin-dashboard/presentation/pages/admin-dashboard-page";
import { AdminMarginPage } from "../../modules/admin-margin/presentation/pages/admin-margin-page";
import { AdminAiExtractionsPage } from "../../modules/admin-ai-extractions/presentation/pages/admin-ai-extractions-page";
import { HomePageSkeleton } from "../common/home-page-skeleton";
import { MyChangeRequestsPage } from "../../modules/change-requests/presentation/pages/my-change-requests-page";
import { ChangeRequestDetailsPage } from "../../modules/change-requests/presentation/pages/change-request-details-page";
import { EditBuyerProfileRequestPage } from "../../modules/change-requests/presentation/pages/edit-buyer-profile-request-page";
import { EditProductRequestPage } from "../../modules/change-requests/presentation/pages/edit-product-request-page";
import { AdminChangeRequestsPage } from "../../modules/admin-change-requests/presentation/pages/admin-change-requests-page";
import { AdminChangeRequestReviewPage } from "../../modules/admin-change-requests/presentation/pages/admin-change-request-review-page";
import { AdminUserChangeRequestsPage } from "../../modules/admin-change-requests/presentation/pages/admin-user-change-requests-page";
import { MyComparisonsPage } from "../../modules/comparisons/presentation/pages/my-comparisons-page";
import MyConversationsPage from "../../modules/ai-search/presentation/pages/my-conversations-page";
import {
  IconAlertTriangle,
  IconBell,
  IconBolt,
  IconCash,
  IconChartBar,
  IconCreditCard,
  IconFileDescription,
  IconFileText,
  IconLayoutDashboard,
  IconPackage,
  IconSettings,
  IconShield,
  IconShieldCheck,
  IconShoppingCart,
  IconSparkles,
  IconStar,
  IconUsers,
} from "@tabler/icons-react";
import { AdminOrdersPage } from "../../modules/admin-orders/presentation/pages/admin-orders-page";
import { AdminOrderDetailsPage } from "../../modules/admin-orders/presentation/pages/admin-order-details-page";
import { WarrantyClaimsPage } from "../../modules/warranty-claims/presentation/pages/warranty-claims-page";
import { WarrantyClaimDetailsPage } from "../../modules/warranty-claims/presentation/pages/warranty-claim-details-page";
import { BuyerWarrantyClaimsPage } from "../../modules/warranty-claims/presentation/pages/buyer-warranty-claims-page";
import { BuyerWarrantyClaimDetailPage } from "../../modules/warranty-claims/presentation/pages/buyer-warranty-claim-detail-page";
import { AdminOrderDisputesPage } from "../../modules/order-disputes/presentation/pages/admin-order-disputes-page";
import { AdminOrderDisputeDetailsPage } from "../../modules/order-disputes/presentation/pages/admin-order-dispute-details-page";
import { AdminWarrantyClaimsPage } from "../../modules/warranty-claims/presentation/pages/admin-warranty-claims-page";
import { AdminWarrantyClaimDetailPage } from "../../modules/warranty-claims/presentation/pages/admin-warranty-claim-detail-page";
import { AdminUsersPage } from "../../modules/admin-users/presentation/pages/admin-users-page";
import { AdminUserDetailPage } from "../../modules/admin-users/presentation/pages/admin-user-detail-page";
import { useDeliveryPushNotifications } from "../../modules/inbox/hooks/use-delivery-push-notifications";
import { usePushPreference } from "../../hooks/use-push-preference";

export type AppRoute = {
  path: string;
  component: ComponentType;
  title: string;
  icon: (current: boolean) => JSX.Element;
  hideInNavigation?: boolean;
  children?: AppRoute[];
  section?: string;
};

const AppRouteList = (
  user?: UserDto,
  isBuyer?: boolean,
  isSeller?: boolean,
) => {
  const isAdmin = user?.role === Role.Admin;
  const profile = {
    path: AppRoutes.PROFILE,
    component: SettingsPage,
    icon: ProfileIcon,
    title: "Profile",
    hideInNavigation: true,
  };

  const products = {
    path: AppRoutes.PRODUCTS,
    component: ProductsPage,
    icon: DashboardIcon,
    title: "Products",
  };

  const productDetails = {
    path: AppRoutes.PRODUCT_DETAILS,
    component: ProductDetailsPage,
    icon: () => <IconFileDescription />,
    title: "Product Details",
    hideInNavigation: true,
  };
  const productsCompare = {
    path: AppRoutes.PRODUCTS_COMPARE,
    component: ProductsComparePage,
    icon: () => <IconFileDescription />,
    title: "Compare Products",
    hideInNavigation: true,
  };

  const sellerDashboard = {
    path: AppRoutes.DASHBOARD,
    component: SellerDashboardPage,
    icon: () => <IconLayoutDashboard size={20} />,
    title: "Dashboard",
    section: "Main",
  };

  const myProducts = {
    path: AppRoutes.MY_PRODUCTS,
    component: MyProductsPage,
    icon: () => <IconPackage size={20} />,
    title: "Products",
    section: "Catalog",
  };

  const myWarranties = {
    path: AppRoutes.MY_WARRANTIES,
    component: MyWarrantiesPage,
    icon: () => <IconShield size={20} />,
    title: "Warranties",
    section: "Catalog",
  };

  const myProductDetails = {
    path: AppRoutes.MY_PRODUCT_DETAILS,
    component: MyProductDetailsPage,
    icon: () => <IconPackage />,
    title: "My Product Details",
    hideInNavigation: true,
  };

  const myProductNew = {
    path: AppRoutes.MY_PRODUCT_NEW,
    component: MyProductNewPage,
    icon: () => <IconPackage />,
    title: "Add Product",
    hideInNavigation: true,
  };

  const myProductRequestEdit = {
    path: AppRoutes.MY_PRODUCT_REQUEST_EDIT,
    component: MyProductNewPage,
    icon: () => <IconPackage />,
    title: "Edit Draft",
    hideInNavigation: true,
  };

  const myProductBulkUploadPreview = {
    path: AppRoutes.MY_PRODUCT_BULK_UPLOAD_PREVIEW,
    component: MyProductNewPage,
    icon: () => <IconPackage />,
    title: "Preview Product",
    hideInNavigation: true,
  };

  const myProductBulkUploadResults = {
    path: AppRoutes.MY_PRODUCT_BULK_UPLOAD_RESULTS,
    component: MyProductBulkUploadResultsPage,
    icon: () => <IconPackage />,
    title: "Import Results",
    hideInNavigation: true,
  };

  const myProductBulkUpload = {
    path: AppRoutes.MY_PRODUCT_BULK_UPLOAD,
    component: MyProductBulkUploadPage,
    icon: () => <IconPackage />,
    title: "Bulk Product Upload",
    hideInNavigation: true,
  };

  const myProductEdit = {
    path: AppRoutes.MY_PRODUCT_EDIT,
    component: MyProductEditPage,
    icon: () => <IconPackage />,
    title: "Edit Product",
    hideInNavigation: true,
  };

  const partners = {
    path: AppRoutes.PARTNERS,
    component: PartnersPage,
    icon: () => <IconUsers />,
    title: "Partners",
    section: "Settings",
  };

  const createPartner = {
    path: AppRoutes.CREATE_PARTNER,
    component: CreatePartnerPage,
    icon: () => <IconUsers />,
    title: "Create Partner",
    hideInNavigation: true,
  };

  const partnerDetails = {
    path: AppRoutes.PARTNER_DETAILS,
    component: PartnerDetailsPage,
    icon: () => <IconUsers />,
    title: "Partner Details",
    hideInNavigation: true,
  };

  const orders = {
    path: AppRoutes.ORDERS,
    component: SalesPage,
    icon: () => <IconShoppingCart />,
    title: "Orders",
  };

  const orderDetails = {
    path: AppRoutes.ORDER_DETAILS,
    component: SaleDetailsPage,
    icon: () => <IconShoppingCart />,
    title: "Order Details",
    hideInNavigation: true,
  };

  const checkout = {
    path: AppRoutes.CHECKOUT,
    component: CheckoutPage,
    icon: () => <IconShoppingCart />,
    title: "Checkout",
    hideInNavigation: true,
  };

  const adminDocuments = {
    path: AppRoutes.ADMIN_DOCUMENTS,
    component: AdminDocumentsPage,
    icon: () => <IconFileDescription />,
    title: "Document Review",
    section: "Approvals",
  };

  const userDocumentsReview = {
    path: AppRoutes.ADMIN_DOCUMENTS_USER,
    component: UserDocumentsReviewPage,
    icon: () => <IconFileDescription />,
    title: "User Documents Review",
    hideInNavigation: true,
  };

  const productRequestDetails = {
    path: AppRoutes.PRODUCT_REQUEST_DETAILS,
    component: ProductRequestDetailsPage,
    icon: () => <IconPackage />,
    title: "Product Request Details",
    hideInNavigation: true,
  };

  const adminProductRequestDetails = {
    path: AppRoutes.ADMIN_PRODUCT_REQUEST_DETAILS,
    component: ProductRequestDetailsPage,
    icon: () => <IconPackage />,
    title: "Admin Product Request Details",
    hideInNavigation: true,
  };

  const adminProductRequests = {
    path: AppRoutes.ADMIN_PRODUCT_REQUESTS,
    component: AdminProductRequestsPage,
    icon: () => <IconPackage />,
    title: "Product Review",
    section: "Approvals",
  };

  const userProductRequestsReview = {
    path: AppRoutes.ADMIN_PRODUCT_REQUESTS_USER,
    component: UserProductRequestsReviewPage,
    icon: () => <IconPackage />,
    title: "User Product Requests Review",
    hideInNavigation: true,
  };

  const adminProductRequestReview = {
    path: AppRoutes.ADMIN_PRODUCT_REQUEST_REVIEW,
    component: AdminProductRequestReviewPage,
    icon: () => <IconPackage />,
    title: "Product Request Review",
    hideInNavigation: true,
  };

  const loyaltyPoints = {
    path: AppRoutes.LOYALTY_POINTS,
    component: LoyaltyPointsPage,
    icon: () => <IconStar />,
    title: "Loyalty Points",
  };

  const adminLoyaltyConfig = {
    path: AppRoutes.ADMIN_LOYALTY_CONFIG,
    component: AdminLoyaltyConfigPage,
    icon: () => <IconStar />,
    title: "Overview",
    section: "Loyalty Points",
  };

  const adminLoyaltyPromotions = {
    path: AppRoutes.ADMIN_LOYALTY_PROMOTIONS,
    component: AdminLoyaltyPromotionsPage,
    icon: () => <IconBolt />,
    title: "Promotions",
    section: "Loyalty Points",
  };

  const adminLoyaltyPromotionNew = {
    path: AppRoutes.ADMIN_LOYALTY_PROMOTION_NEW,
    component: AdminLoyaltyPromotionEditPage,
    icon: () => <IconBolt />,
    title: "New Promotion",
    hideInNavigation: true,
  };

  const adminLoyaltyPromotionDetail = {
    path: AppRoutes.ADMIN_LOYALTY_PROMOTION_DETAIL,
    component: AdminLoyaltyPromotionDetailPage,
    icon: () => <IconBolt />,
    title: "Promotion",
    hideInNavigation: true,
  };

  const adminLoyaltyPromotionEdit = {
    path: AppRoutes.ADMIN_LOYALTY_PROMOTION_EDIT,
    component: AdminLoyaltyPromotionEditPage,
    icon: () => <IconBolt />,
    title: "Edit Promotion",
    hideInNavigation: true,
  };

  const adminLoyaltySettings = {
    path: AppRoutes.ADMIN_LOYALTY_SETTINGS,
    component: AdminLoyaltySettingsPage,
    icon: () => <IconSettings size={20} />,
    title: "Configuration",
    section: "Loyalty Points",
  };

  const adminLoyaltyAnalytics = {
    path: AppRoutes.ADMIN_LOYALTY_ANALYTICS,
    component: AdminLoyaltyAnalyticsPage,
    icon: () => <IconChartBar size={20} />,
    title: "Analytics",
    section: "Loyalty Points",
  };

  const adminLoyaltyTransactionDetail = {
    path: AppRoutes.ADMIN_LOYALTY_TRANSACTION,
    component: AdminLoyaltyTransactionDetailPage,
    icon: () => <IconStar />,
    title: "Transaction Detail",
    hideInNavigation: true,
  };

  const adminLoyaltyRedemptionDetail = {
    path: AppRoutes.ADMIN_LOYALTY_REDEMPTION,
    component: AdminLoyaltyRedemptionDetailPage,
    icon: () => <IconStar />,
    title: "Redemption Detail",
    hideInNavigation: true,
  };

  const adminUsers = {
    path: AppRoutes.ADMIN_USERS,
    component: AdminUsersPage,
    icon: () => <IconUsers size={20} />,
    title: "User Management",
    section: "Settings",
  };

  const adminUserDetails = {
    path: AppRoutes.ADMIN_USER_DETAILS,
    component: AdminUserDetailPage,
    icon: () => <IconUsers size={20} />,
    title: "User Details",
    hideInNavigation: true,
  };

  const adminCatalog = {
    path: AppRoutes.ADMIN_CATALOG,
    component: AdminCatalogPage,
    icon: () => <IconSettings size={20} />,
    title: "Catalog Configuration",
    section: "Settings",
  };

  const adminFeaturedDeals = {
    path: AppRoutes.ADMIN_FEATURED_DEALS,
    component: AdminFeaturedDealsPage,
    icon: () => <IconStar size={20} />,
    title: "Deal of the Week",
    section: "Main",
  };

  // TODO: re-enable in sidebar once margin data is ready
  const adminMargin = {
    path: AppRoutes.ADMIN_MARGIN_DASHBOARD,
    component: AdminMarginPage,
    icon: () => <IconChartBar size={20} />,
    title: "Margin",
    hideInNavigation: true,
  };

  const adminAiExtractions = {
    path: AppRoutes.ADMIN_AI_EXTRACTIONS,
    component: AdminAiExtractionsPage,
    icon: () => <IconSparkles size={20} />,
    title: "AI usage",
    section: "Finance",
  };

  const adminPayments = {
    path: AppRoutes.ADMIN_PAYMENTS,
    component: AdminPaymentsPage,
    icon: () => <IconCash />,
    title: "Payments",
    section: "Finance",
  };

  const adminSupplierBills = {
    path: AppRoutes.ADMIN_SUPPLIER_BILLS,
    component: AdminSupplierBillsPage,
    icon: () => <IconCash />,
    title: "Supplier bills",
    hideInNavigation: true,
  };

  const adminPaymentDetails = {
    path: AppRoutes.ADMIN_PAYMENT_DETAILS,
    component: AdminPaymentDetailsPage,
    icon: () => <IconCash />,
    title: "Payment Details",
    hideInNavigation: true,
  };

  const purchaseOrders = {
    path: AppRoutes.PURCHASE_ORDERS,
    component: PurchaseOrdersPage,
    icon: () => <IconShoppingCart size={20} />,
    title: "Orders",
    section: "Commerce",
  };

  const confirmDelivery = {
    path: AppRoutes.CONFIRM_DELIVERY,
    component: ConfirmDeliveryPage,
    icon: () => <IconShoppingCart />,
    title: "Confirm Delivery",
    hideInNavigation: true,
  };

  const myChangeRequests = {
    path: AppRoutes.CHANGE_REQUESTS,
    component: MyChangeRequestsPage,
    icon: () => <IconFileDescription />,
    title: "My Change Requests",
    hideInNavigation: true,
  };

  const changeRequestDetails = {
    path: AppRoutes.CHANGE_REQUEST_DETAILS,
    component: ChangeRequestDetailsPage,
    icon: () => <IconFileDescription />,
    title: "Change Request Details",
    hideInNavigation: true,
  };

  const editBuyerProfileRequest = {
    path: AppRoutes.CHANGE_REQUEST_NEW_BUYER_PROFILE,
    component: EditBuyerProfileRequestPage,
    icon: () => <IconFileDescription />,
    title: "Edit Buyer Profile",
    hideInNavigation: true,
  };

  const editProductRequest = {
    path: AppRoutes.CHANGE_REQUEST_NEW_PRODUCT,
    component: EditProductRequestPage,
    icon: () => <IconFileDescription />,
    title: "Edit Product",
    hideInNavigation: true,
  };

  const adminChangeRequests = {
    path: AppRoutes.ADMIN_CHANGE_REQUESTS,
    component: AdminChangeRequestsPage,
    icon: () => <IconFileDescription />,
    title: "Change Requests",
    section: "Approvals",
  };

  const adminChangeRequestReview = {
    path: AppRoutes.ADMIN_CHANGE_REQUEST_REVIEW,
    component: AdminChangeRequestReviewPage,
    icon: () => <IconFileDescription />,
    title: "Change Request Review",
    hideInNavigation: true,
  };

  const adminUserChangeRequests = {
    path: AppRoutes.ADMIN_CHANGE_REQUESTS_USER,
    component: AdminUserChangeRequestsPage,
    icon: () => <IconFileDescription />,
    title: "User change requests",
    hideInNavigation: true,
  };

  const inbox = {
    path: AppRoutes.INBOX,
    component: InboxPage,
    icon: () => <IconBell size={20} />,
    title: "Notifications",
    hideInNavigation: true,
  };

  const commonRoutes = [productDetails, checkout, profile, inbox];

  const adminDashboard = {
    path: AppRoutes.ADMIN_DASHBOARD,
    component: AdminDashboardPage,
    icon: () => <IconLayoutDashboard size={20} />,
    title: "Dashboard",
    section: "Main",
  };

  const adminOrders = {
    path: AppRoutes.ADMIN_ORDERS,
    component: AdminOrdersPage,
    icon: () => <IconShoppingCart size={20} />,
    title: "Orders",
    section: "Commerce",
  };

  const adminOrderDetails = {
    path: AppRoutes.ADMIN_ORDER_DETAILS,
    component: AdminOrderDetailsPage,
    icon: () => <IconShoppingCart size={20} />,
    title: "Order Details",
    hideInNavigation: true,
  };

  const adminOrderDisputes = {
    path: AppRoutes.ADMIN_ORDER_DISPUTES,
    component: AdminOrderDisputesPage,
    icon: () => <IconAlertTriangle size={20} />,
    title: "Order Disputes",
    section: "Commerce",
  };

  const adminOrderDisputeDetails = {
    path: AppRoutes.ADMIN_ORDER_DISPUTE_DETAILS,
    component: AdminOrderDisputeDetailsPage,
    icon: () => <IconAlertTriangle size={20} />,
    title: "Order Dispute",
    hideInNavigation: true,
  };

  const adminWarrantyClaims = {
    path: AppRoutes.ADMIN_WARRANTY_CLAIMS,
    component: AdminWarrantyClaimsPage,
    icon: () => <IconShieldCheck size={20} />,
    title: "Warranty Claims",
    section: "Commerce",
  };

  const adminWarrantyClaimDetails = {
    path: AppRoutes.ADMIN_WARRANTY_CLAIM_DETAILS,
    component: AdminWarrantyClaimDetailPage,
    icon: () => <IconShieldCheck size={20} />,
    title: "Warranty Claim",
    hideInNavigation: true,
  };

  const adminProducts = {
    path: AppRoutes.ADMIN_PRODUCTS,
    component: ProductsPage,
    icon: () => <IconPackage size={20} />,
    title: "Products",
    section: "Catalog",
  };

  const adminProductDetails = {
    path: AppRoutes.ADMIN_PRODUCT_DETAILS,
    component: ProductDetailsPage,
    icon: () => <IconFileDescription />,
    title: "Product Details",
    hideInNavigation: true,
  };

  const adminPurchaseOrders = {
    path: AppRoutes.ADMIN_PURCHASE_ORDERS,
    component: PurchaseOrdersPage,
    icon: () => <IconShoppingCart />,
    title: "Purchase Orders",
    section: "Commerce",
  };

  const adminPurchaseOrderDetails = {
    path: AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS,
    component: PurchaseOrderDetailsPage,
    icon: () => <IconShoppingCart />,
    title: "Purchase Order Details",
    hideInNavigation: true,
  };

  const adminRoutes = [
    adminDashboard,
    adminFeaturedDeals,
    adminDocuments,
    adminProductRequests,
    adminChangeRequests,
    adminOrders,
    adminOrderDisputes,
    adminOrderDisputeDetails,
    adminWarrantyClaims,
    adminWarrantyClaimDetails,
    adminMargin,
    adminPayments,
    adminLoyaltyConfig,
    adminLoyaltyPromotions,
    adminLoyaltyPromotionNew,
    adminLoyaltyPromotionDetail,
    adminLoyaltyPromotionEdit,
    adminLoyaltySettings,
    adminLoyaltyAnalytics,
    adminAiExtractions,
    adminLoyaltyTransactionDetail,
    adminLoyaltyRedemptionDetail,
    partners,
    createPartner,
    partnerDetails,
    adminUsers,
    adminUserDetails,
    adminCatalog,
    productRequestDetails,
    adminProductRequestDetails,
    userDocumentsReview,
    userProductRequestsReview,
    adminProductRequestReview,
    adminOrderDetails,
    adminChangeRequestReview,
    adminUserChangeRequests,
    adminPurchaseOrders,
    adminPurchaseOrderDetails,
    confirmDelivery,
    adminProducts,
    adminProductDetails,
    adminSupplierBills,
    adminPaymentDetails,
    ...commonRoutes,
  ];

  const aiSearch = {
    path: AppRoutes.AI_SEARCH,
    component: AiSearchPage,
    icon: DashboardIcon,
    title: "AI Search",
  };

  const dashboard = {
    path: AppRoutes.DASHBOARD,
    component: DashboardPage,
    icon: DashboardIcon,
    title: "Dashboard",
  };

  const wishlist = {
    path: AppRoutes.WISHLIST,
    component: WishlistPage,
    icon: () => <IconStar />,
    title: "Wishlist",
    hideInNavigation: true,
  };

  const myComparisons = {
    path: AppRoutes.MY_COMPARISONS,
    component: MyComparisonsPage,
    icon: () => <IconFileDescription />,
    title: "My Comparisons",
    hideInNavigation: true,
  };

  const myConversations = {
    path: AppRoutes.MY_CONVERSATIONS,
    component: MyConversationsPage,
    icon: () => <IconFileDescription />,
    title: "My Conversations",
    hideInNavigation: true,
  };

  const account = {
    path: AppRoutes.ACCOUNT,
    component: SettingsPage,
    icon: ProfileIcon,
    title: "Account",
    hideInNavigation: true,
  };

  const quotations = {
    path: AppRoutes.QUOTATIONS,
    component: QuotationsPage,
    icon: () => <IconFileDescription />,
    title: "Quotations",
  };

  const quotationDetails = {
    path: AppRoutes.QUOTATION_DETAILS,
    component: QuotationDetailPage,
    icon: () => <IconFileText size={20} />,
    title: "Quotation Details",
    hideInNavigation: true,
  };

  const buyerWarrantyClaims = {
    path: AppRoutes.MY_WARRANTY_CLAIMS,
    component: BuyerWarrantyClaimsPage,
    icon: () => <IconShieldCheck size={20} />,
    title: "Warranty Claims",
    section: "Orders",
  };

  const buyerWarrantyClaimDetails = {
    path: AppRoutes.MY_WARRANTY_CLAIM_DETAILS,
    component: BuyerWarrantyClaimDetailPage,
    icon: () => <IconShieldCheck size={20} />,
    title: "Warranty Claim",
    hideInNavigation: true,
  };

  const buyerRoutes = [
    aiSearch,
    dashboard,
    products,
    productDetails,
    productsCompare,
    quotations,
    quotationDetails,
    orders,
    orderDetails,
    loyaltyPoints,
    wishlist,
    myComparisons,
    myConversations,
    account,
    myChangeRequests,
    changeRequestDetails,
    editBuyerProfileRequest,
    buyerWarrantyClaims,
    buyerWarrantyClaimDetails,
    ...commonRoutes,
  ];

  const sellerPayments = {
    path: AppRoutes.PAYMENTS,
    component: SellerPaymentsPage,
    icon: () => <IconCreditCard size={20} />,
    title: "Payments",
    section: "Finance",
  };

  const warrantyClaimsRoute = {
    path: AppRoutes.WARRANTY_CLAIMS,
    component: WarrantyClaimsPage,
    icon: () => <IconShieldCheck size={20} />,
    title: "Warranty Claims",
    section: "Operations",
  };

  const warrantyClaimDetailsRoute = {
    path: AppRoutes.WARRANTY_CLAIM_DETAILS,
    component: WarrantyClaimDetailsPage,
    icon: () => <IconShieldCheck size={20} />,
    title: "Warranty Claim",
    hideInNavigation: true,
  };

  const sellerSettings = {
    path: AppRoutes.SETTINGS,
    component: SellerSettingsPage,
    icon: () => <IconSettings size={20} />,
    title: "Settings",
    section: "Account",
  };

  const sellerOrderDetails = {
    path: AppRoutes.PURCHASE_ORDER_DETAILS,
    component: SellerOrderDetailsPage,
    icon: () => <IconShoppingCart size={20} />,
    title: "Order Details",
    hideInNavigation: true,
  };

  const sellerRoutes = [
    sellerDashboard,
    myProducts,
    myWarranties,
    myProductNew,
    myProductRequestEdit,
    myProductBulkUploadPreview,
    myProductBulkUploadResults,
    myProductBulkUpload,
    myProductDetails,
    myProductEdit,
    productRequestDetails,
    purchaseOrders,
    sellerOrderDetails,
    confirmDelivery,
    sellerPayments,
    warrantyClaimsRoute,
    warrantyClaimDetailsRoute,
    { ...myChangeRequests, hideInNavigation: true },
    changeRequestDetails,
    editProductRequest,
    sellerSettings,
    ...commonRoutes,
  ];

  const routes: AppRoute[] = isAdmin
    ? adminRoutes
    : isBuyer
      ? buyerRoutes
      : isSeller
        ? sellerRoutes
        : commonRoutes;

  return routes;
};
// Route guard to redirect sellers away from buyer-only pages.
// `/products` and `/ai-search` aren't part of `sellerRoutes`, so without this
// they'd otherwise fall through to a 404 instead of landing somewhere useful.
const SELLER_BLOCKED_PATHS = new Set<string>([
  AppRoutes.PRODUCTS,
  AppRoutes.AI_SEARCH,
]);
function RouteGuard({
  children,
  isSeller,
  defaultRoute,
}: {
  children: React.ReactNode;
  isSeller: boolean;
  defaultRoute: string;
}) {
  const location = useLocation();
  if (isSeller && SELLER_BLOCKED_PATHS.has(location.pathname)) {
    return <Navigate to={defaultRoute} replace />;
  }
  return <>{children}</>;
}
export function AuthenticatedApp() {
  const { user } = useMe();
  const { isBuyer, isSeller, isLoading: isLoadingPartner } = useMePartner();
  const loadLikedIds = useWishlistStore((s) => s.loadLikedIds);
  const clearWishlist = useWishlistStore((s) => s.clear);
  const isAdmin = user?.role === Role.Admin;
  const [pushEnabled] = usePushPreference();
  // Not gated on isBuyer: the inbox is already scoped to the current user, and
  // the push toggle is offered in seller settings too — gating here meant a
  // seller could enable it and never receive anything.
  useDeliveryPushNotifications(pushEnabled);
  useEffect(() => {
    if (user?.id != null && isBuyer) {
      void loadLikedIds();
    } else {
      clearWishlist();
    }
  }, [user?.id, isBuyer, loadLikedIds, clearWishlist]);
  if (isLoadingPartner) return <HomePageSkeleton />;
  const routes = AppRouteList(user, isBuyer, isSeller);
  const defaultRoute = (() => {
    if (isAdmin) {
      return AppRoutes.ADMIN_DASHBOARD;
    }
    if (isSeller) {
      return AppRoutes.DASHBOARD;
    }
    if (isBuyer) {
      return AppRoutes.DASHBOARD;
    }
    return AppRoutes.PRODUCTS;
  })();
  return (
    <Routes>
      <Route path={AppRoutes.ONBOARDING} element={<OnboardingPage />} />

      <Route
        path={AppRoutes.INDEX}
        element={
          <OnboardingGuard>
            <RouteGuard isSeller={isSeller} defaultRoute={defaultRoute}>
              <AppShell navigation={routes} />
            </RouteGuard>
          </OnboardingGuard>
        }
      >
        <Route index element={<Navigate to={defaultRoute} replace />} />

        {routes?.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<route.component />}
          />
        ))}

        {footerRoutesBuilder()}

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
